import { Mutable } from '@evorait/pace-core';
import { Answers } from '../../../types/answers';
import { isDatePerformed } from '../../../types/date-performed';
import { isDefectiveComponentReplacement } from '../../../types/defective-component-replacement';
import { isDiscrepancyLog } from '../../../types/discrepancy-log';
import { FormsConstants } from '../../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../../../types/functional-element';
import { FunctionalElements } from '../../../types/functional-elements';
import { isMaterialSerialNumber } from '../../../types/material-serial-number';
import { isAstoriaOption, isCosimaOption } from '../../../types/option';
import { RadioOptions } from '../../../types/radio-options';
import {
    isReplacementDateOrLastProcedureDate,
    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION,
} from '../../../types/replacement-or-last-procedure-date';
import { isSignature, Signature } from '../../../types/signature';
import { isRemarksFunctionalElement, isStandardFunctionalElement } from '../../../types/standard-functional-element';
import { SystemCondition } from '../../../types/system-condition';
import { isValue } from '../../../types/value-functional-element';
import { isWorkstepsPerformed } from '../../../types/worksteps-performed';
import { AnswerUtilities } from '../../../utilities/answer-utilities';
import { FormState } from '../../state/form.state';
import { isDropDownOptionsElement } from '../../../types/drop-down-options';

export function setFunctionalElementToYes<F extends FunctionalElement>(f: F): F {
    if (isDiscrepancyLog(f)) {
        return { ...f, disabled: false, value: '' };
    } else if (isSignature(f)) {
        return {
            ...f,
            firstName: f.firstName === FormsConstants.NA ? '' : f.firstName,
            lastName: f.lastName === FormsConstants.NA ? '' : f.lastName,
            email: f.email === FormsConstants.NA ? '' : f.email,
            date: f.date === FormsConstants.NA ? '' : f.date,
            disabled: false,
        };
    } else if (isMaterialSerialNumber(f)) {
        return {
            ...f,
            disabled: false,
            serialValue: f.serialValue === FormsConstants.NA ? '' : f.serialValue,
            materialValue: f.materialValue === FormsConstants.NA ? '' : f.materialValue,
            descriptionValue: f.descriptionValue === FormsConstants.NA ? '' : f.descriptionValue,
        };
    } else if (isDatePerformed(f)) {
        return { ...f, disabled: false, date: f.date === FormsConstants.NA ? '' : f.date };
    } else if (isValue(f)) {
        return { ...f, disabled: false, value: f.value === FormsConstants.NA ? '' : f.value };
    } else if (isReplacementDateOrLastProcedureDate(f)) {
        return { ...f, disabled: false, date: f.date === FormsConstants.NA ? '' : f.date };
    } else if (isWorkstepsPerformed(f)) {
        return { ...f };
    } else if (isAstoriaOption(f)) {
        return { ...f };
    } else if (isCosimaOption(f)) {
        return { ...f, choices: { ...f.choices, choiceNegative: { ...f.choices?.choiceNegative, selected: false } } };
    } else if (isStandardFunctionalElement(f)) {
        return { ...f, remarks: f.remarks === FormsConstants.NA ? '' : f.remarks };
    } else if (isDropDownOptionsElement(f)) {
        return { ...f, remarks: f.remarks === FormsConstants.NA ? '' : f.remarks };
    } else if (isRemarksFunctionalElement(f)) {
        return { ...f, remarks: f.remarks === FormsConstants.NA ? '' : f.remarks };
    } else if (isDefectiveComponentReplacement(f)) {
        return {
            ...f,
            disabled: true,
            swapOutComponent: {
                ...f.swapOutComponent,
                matNo: f.swapOutComponent.matNo === FormsConstants.NA ? '' : f.swapOutComponent.matNo,
                serialNo: f.swapOutComponent.serialNo === FormsConstants.NA ? '' : f.swapOutComponent.serialNo,
            },
            swapInComponent: {
                ...f.swapInComponent,
                matNo: f.swapInComponent.matNo === FormsConstants.NA ? '' : f.swapInComponent.matNo,
                serialNo: f.swapInComponent.serialNo === FormsConstants.NA ? '' : f.swapInComponent.serialNo,
            },
            repairTime: f.repairTime === FormsConstants.NA ? '' : f.repairTime,
            remarks: f.remarks === FormsConstants.NA ? '' : f.remarks,
        };
    }

    throw new Error(`FormReducer: setFunctionalElementToYes: Encountered unknown functional element type ${f.type}`);
}

export function setFunctionalElementToNo<F extends FunctionalElement>(f: F): F {
    if (isDiscrepancyLog(f)) {
        return { ...f, disabled: true, value: '' };
    } else if (isSignature(f)) {
        return {
            ...f,
            firstName: f.firstName === FormsConstants.NA ? '' : f.firstName,
            lastName: f.lastName === FormsConstants.NA ? '' : f.lastName,
            email: f.email === FormsConstants.NA ? '' : f.email,
            date: f.date === FormsConstants.NA ? '' : f.date,
            disabled: false,
        };
    } else if (isMaterialSerialNumber(f)) {
        return {
            ...f,
            disabled: false,
            serialValue: f.serialValue === FormsConstants.NA ? '' : f.serialValue,
            materialValue: f.materialValue === FormsConstants.NA ? '' : f.materialValue,
            descriptionValue: f.descriptionValue === FormsConstants.NA ? '' : f.descriptionValue,
        };
    } else if (isDatePerformed(f)) {
        return { ...f, disabled: false, date: f.date === FormsConstants.NA ? '' : f.date };
    } else if (isValue(f)) {
        return { ...f, disabled: false, value: f.value === FormsConstants.NA ? '' : f.value };
    } else if (isReplacementDateOrLastProcedureDate(f)) {
        return { ...f, disabled: true, date: '', selected: REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE };
    } else if (isWorkstepsPerformed(f)) {
        return { ...f };
    } else if (isAstoriaOption(f) || isCosimaOption(f)) {
        return { ...f };
    } else if (isStandardFunctionalElement(f)) {
        return { ...f };
    } else if (isDropDownOptionsElement(f)) {
        return { ...f };
    } else if (isRemarksFunctionalElement(f)) {
        return { ...f, remarks: f.remarks === FormsConstants.NA ? '' : f.remarks };
    } else if (isDefectiveComponentReplacement(f)) {
        return {
            ...f,
            disabled: false,
            swapOutComponent: {
                ...f.swapOutComponent,
                matNo: f.swapOutComponent.matNo === FormsConstants.NA ? '' : f.swapOutComponent.matNo,
                serialNo: f.swapOutComponent.serialNo === FormsConstants.NA ? '' : f.swapOutComponent.serialNo,
            },
            swapInComponent: {
                ...f.swapInComponent,
                matNo: f.swapInComponent.matNo === FormsConstants.NA ? '' : f.swapInComponent.matNo,
                serialNo: f.swapInComponent.serialNo === FormsConstants.NA ? '' : f.swapInComponent.serialNo,
            },
            repairTime: f.repairTime === FormsConstants.NA ? '' : f.repairTime,
            remarks: f.remarks === FormsConstants.NA ? '' : f.remarks,
        };
    }

    throw new Error(`FormReducer: setFunctionalElementToNo: Encountered unknown functional element type ${f.type}`);
}

export function resetFunctionalElement<F extends FunctionalElement>(f: F): F {
    if (isDiscrepancyLog(f)) {
        return { ...f, value: '' };
    } else if (isSignature(f)) {
        return {
            ...f,
            signature: '',
            signatureDataPoints: [],
            firstName: '',
            lastName: '',
            email: '',
            date: '',
        };
    } else if (isMaterialSerialNumber(f)) {
        return {
            ...f,
            serialValue: '',
            materialValue: '',
            descriptionValue: '',
            disabled: false,
            remarks: '',
        };
    } else if (isDatePerformed(f)) {
        return { ...f, disabled: false, date: '', remarks: '' };
    } else if (isValue(f)) {
        return { ...f, value: '', remarks: '' };
    } else if (isReplacementDateOrLastProcedureDate(f)) {
        return {
            ...f,
            date: '',
            selected: REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE,
        };
    } else if (isWorkstepsPerformed(f)) {
        return { ...f };
    } else if (isAstoriaOption(f) || isCosimaOption(f)) {
        return { ...f };
    } else if (isStandardFunctionalElement(f) || isRemarksFunctionalElement(f)) {
        return { ...f, remarks: '' };
    } else if (isDropDownOptionsElement(f)) {
        return { ...f, remarks: '' };
    } else if (isDefectiveComponentReplacement(f)) {
        return {
            ...f,
            swapInComponent: {
                ...f.swapInComponent,
                matNo: '',
                serialNo: '',
            },
            swapOutComponent: {
                ...f.swapOutComponent,
                matNo: '',
                serialNo: '',
            },
            repairTime: '',
            remarks: '',
        };
    }

    throw new Error(`FormReducer: resetFunctionalElement: Encountered unknown functional element type ${f.type}`);
}

export function setFunctionalElementToNA<F extends FunctionalElement>(f: F): F {
    if (isDiscrepancyLog(f)) {
        return { ...f, disabled: true, value: FormsConstants.NA };
    } else if (isSignature(f)) {
        return {
            ...f,
            disabled: true,
            signature: '',
            signatureDataPoints: [],
            firstName: FormsConstants.NA,
            lastName: FormsConstants.NA,
            email: FormsConstants.NA,
            date: FormsConstants.NA,
        };
    } else if (isMaterialSerialNumber(f)) {
        return {
            ...f,
            disabled: true,
            serialValue: f.serialValue.length === 0 ? FormsConstants.NA : f.serialValue,
            materialValue: f.materialValue.length === 0 ? FormsConstants.NA : f.materialValue,
            descriptionValue: f.descriptionValue.length === 0 ? FormsConstants.NA : f.descriptionValue,
        };
    } else if (isDatePerformed(f)) {
        return { ...f, disabled: true, date: FormsConstants.NA };
    } else if (isValue(f)) {
        return { ...f, disabled: true, value: FormsConstants.NA, disabledNACheckbox: true };
    } else if (isReplacementDateOrLastProcedureDate(f)) {
        return {
            ...f,
            disabled: true,
            date: FormsConstants.NA,
            selected: REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE,
        };
    } else if (isWorkstepsPerformed(f)) {
        return { ...f };
    } else if (isAstoriaOption(f) || isCosimaOption(f)) {
        return { ...f };
    } else if (isStandardFunctionalElement(f)) {
        return { ...f };
    } else if (isDropDownOptionsElement(f)) {
        return { ...f };
    } else if (isRemarksFunctionalElement(f)) {
        return { ...f, remarks: f.remarks?.length === 0 ? FormsConstants.NA : f.remarks };
    } else if (isDefectiveComponentReplacement(f)) {
        return {
            ...f,
            disabled: true,
        };
    }

    throw new Error(`FormReducer: setFunctionalElementToNA: Encountered unknown functional element type ${f.type}`);
}

export function setFunctionalElementToNAWithoutDisablingCheckbox<F extends FunctionalElement>(f: F): F {
    let functionalElement = setFunctionalElementToNA(f);
    if (isValue(f)) {
        functionalElement = { ...functionalElement, disabledNACheckbox: false };
    }
    return functionalElement;
}

/**
 * RegExp-escapes all characters in the given string.
 *
 * @param s the inputted string
 * @returns string
 */
export function regExpEscape(s: string): string {
    return s.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}

export function wildcardToRegExp(s: string): RegExp {
    return new RegExp('^' + s.split(/\*+/).map(regExpEscape).join('.*') + '$');
}

export function isTechnicianSignature(s: Signature): boolean {
    return s.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE || s.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE;
}

export function invalidateSignatures(state: Readonly<FormState>): Readonly<FormState> {
    // system condition
    const oldSystemCondition = state.systemCondition;
    const newSystemCondition: SystemCondition | undefined =
        typeof oldSystemCondition !== 'undefined'
            ? {
                  ...oldSystemCondition,
                  customerSignature: {
                      ...oldSystemCondition.customerSignature,
                      signature: '',
                      signatureDataPoints: [],
                  },
              }
            : undefined;

    // other signatures
    const newSignatures = Object.values(state.participantCopies[state.selectedParticipantCopyId].functionalElements)
        .filter(isSignature)
        .map((s) => {
            return {
                ...s,
                signature: isTechnicianSignature(s) ? s.signature : '',
                signatureDataPoints: isTechnicianSignature(s) ? s.signatureDataPoints : [],
            };
        })
        .reduce((signatures, signature) => {
            signatures[signature.id] = signature;
            return signatures;
        }, {} as Mutable<FunctionalElements>);

    // assemble state
    const newState: Readonly<FormState> = {
        ...state,
        systemCondition: newSystemCondition,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                functionalElements: {
                    ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
                    ...newSignatures,
                },
            },
        },
    };

    return newState;
}

export function updateStateAfterContentChanges(
    state: Readonly<FormState>,
    newFunctionalElements: FunctionalElements,
    newOptions?: RadioOptions,
): Readonly<FormState> {
    if (typeof newOptions === 'undefined') {
        newOptions = state.participantCopies[state.selectedParticipantCopyId].options;
    }

    const newFormWithoutAnswers: Readonly<FormState> = invalidateSignatures({
        ...state,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                options: newOptions,
                functionalElements: newFunctionalElements,
            },
        },
    });

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        newFormWithoutAnswers.selectedParticipantCopyId,
        newFormWithoutAnswers.content,
        newFormWithoutAnswers.checkpoints,
        newFormWithoutAnswers.participantCopies[newFormWithoutAnswers.selectedParticipantCopyId].options,
        newFormWithoutAnswers.participantCopies[newFormWithoutAnswers.selectedParticipantCopyId].functionalElements,
        newFormWithoutAnswers.checkpointDependencies,
        newFormWithoutAnswers.typeFilters,
        newFormWithoutAnswers.formFilters,
        newFormWithoutAnswers.enableManualIndividualization,
        newFormWithoutAnswers.type,
    );

    return {
        ...newFormWithoutAnswers,
        participantCopies: {
            ...newFormWithoutAnswers.participantCopies,
            [newFormWithoutAnswers.selectedParticipantCopyId]: {
                ...newFormWithoutAnswers.participantCopies[newFormWithoutAnswers.selectedParticipantCopyId],
                answers: newAnswers,
            },
        },
    };
}
